import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { useStore } from '../store';
import { TAB_GROUPS } from '../tabPalette';
import InvoiceForm from './Peak/InvoiceForm';
import ReceiptForm from './Peak/ReceiptForm';
import { docKindOf, docNoSplitter, loadDocSeries, useDocSeries } from '../utils/docSeries';

// ── Quotation, invoice and receipt numbers (Calem, Bugify #b4cccf02) ──────────────────────────────────────────────────
// "Anywhere in the SLF system where it displays the QO-, IV-, or RE- … They should all now be in blue color. They should
// all be linked (clickable pop-up) back to the original quotation, invoice, or receipt." A number is drawn in the top
// menu's Quotation / Invoice / Receipt blue, TAB_GROUPS.peak.color, as the Peak lists' own Doc No. already was (docNoColor
// there, report 490ba173); a tick, an icon or the words around it keep their own colour. A click opens the document's
// own form, the one its list opens — InvoiceForm for a quotation or an invoice, ReceiptForm for a receipt — over the page
// or pop-up the number sits in, and closing it goes back there. The click stops at the number, so the row, chip or button
// it sits in doesn't do its own thing as well.
// `no` may be a sentence or a list with numbers in it: each number is drawn so, the rest of the text as it was. A place
// that knows the document's id passes it — with `kind` where the text might be another document's number, as a bank
// row's receipt number falls back to the quotation's — and one that already opens the document itself passes `onOpen`
// (the Peak lists' rows, which reload after a save); otherwise the number is looked up on click (GET
// /api/invoices/doc-by-no). Without the Quotation / Invoice / Receipt module a number is blue but opens nothing: its form
// would be refused, and a refusal raises an admin alert.
type Kind = 'quotation' | 'invoice' | 'receipt';
// A number = one of the workspace's prefixes (utils/docSeries.ts, set in Admin → Companies), the firm letter and the
// digits. split() keeps what it matched at the odd places; one with no digit in it (a prefix followed by a word) is not
// a number.
const isNo = (p: string, i: number) => i % 2 === 1 && /\d/.test(p);

// The open forms, newest on top, drawn by the one DocNoHost (mounted once, in App) rather than by the number: a pop-up
// the number sat in may close on the first click outside it, and must not take the form with it. Above every pop-up.
let seq = 0;
let openDoc: ((d: { kind: Kind; id: string }) => void) | null = null;

export function DocNoHost() {
  const [docs, setDocs] = useState<{ key: number; kind: Kind; id: string }[]>([]);
  // Mounted once in App: fetch the prefixes as soon as someone is signed in, before the first number is drawn.
  const userId = useStore(s => s.me?.userId);
  useEffect(() => { if (userId) void loadDocSeries(true); }, [userId]);
  useEffect(() => {
    openDoc = d => setDocs(o => [...o, { ...d, key: ++seq }]);
    return () => { openDoc = null; };
  }, []);
  if (!docs.length) return null;
  const close = (key: number) => setDocs(o => o.filter(d => d.key !== key));
  return createPortal(
    // The class is for the one thing a stylesheet has to say about these forms: on a narrow screen — a quotation,
    // invoice or receipt number pressed in the installed chat app (report 8c5f3514) — the 880px form is put at the
    // top left of its own dark and that dark scrolls, so the whole of it can be reached. See global.css.
    <div className="mf-docno-host" style={{ position: 'fixed', top: 0, left: 0, zIndex: 13100 }}>
      {docs.map(d => d.kind === 'receipt'
        ? <ReceiptForm key={d.key} receiptId={d.id} onClose={() => close(d.key)} onSaved={() => {}} />
        : <InvoiceForm key={d.key} invoiceId={d.id} kind={d.kind === 'quotation' ? 'quotation' : 'invoice'} onClose={() => close(d.key)} onSaved={() => {}} />)}
    </div>,
    document.body,
  );
}

export default function DocNoLink({ no, id, kind, onOpen, style }: {
  no?: string; id?: string; kind?: string; onOpen?: () => void; style?: React.CSSProperties;
}) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const can = me?.role === 'admin' || (me?.modules || []).includes('peak');
  useDocSeries();
  const split = docNoSplitter();
  const parts = split ? String(no ?? '').split(split) : [String(no ?? '')];
  const count = parts.filter(isNo).length;
  const open = async (e: React.MouseEvent, n: string) => {
    e.stopPropagation();
    if (onOpen) { onOpen(); return; }
    const k = docKindOf(n) as Kind;
    if (id && count === 1 && (!kind || kind === k)) { openDoc?.({ kind: k, id }); return; }
    try {
      const r: any = await api.docByNo(n);
      if (r?.id) openDoc?.({ kind: r.kind, id: r.id });
      else window.alert(t('dn_not_found', { no: n }));
    } catch (er: any) { window.alert(er.message); }
  };
  const body = parts.map((p, i) => isNo(p, i)
    ? <span key={i} role={can ? 'button' : undefined} title={can ? t('dn_open', { no: p }) : undefined}
        onClick={can ? e => { void open(e, p); } : undefined}
        style={{ color: TAB_GROUPS.peak.color, cursor: can ? 'pointer' : undefined }}>{p}</span>
    : p);
  return style ? <span style={style}>{body}</span> : <>{body}</>;
}
